import React, { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cn } from '@utils/utils';
import { SidebarHeader } from './SidebarHeader';
import { Navigation } from './Navigation';
import { SidebarFooter } from './SidebarFooter';
import { Footer } from '../Footer';
import { useMessageCounter } from '@shared/contexts/MessageCounterContext';
import { getPluvialAlerts } from '@feature/pluvialRisk/services/pluvialAlerts.service';
import { PluvialAlert } from '@feature/pluvialRisk/types';
import { ROUTES } from '@shared/constants/routes.constants';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar = React.memo<SidebarProps>(({ isOpen, onToggle }) => {
  const location = useLocation();
  const { setPluvialAlertsCount } = useMessageCounter();

  // Polling global para actualizar badge de alertas pluviales
  useEffect(() => {
    const updatePluvialAlertsBadge = async () => {
      if (location.pathname === ROUTES.MAIN.MONITOR) {
        return;
      }

      try {
        const lastVisit = localStorage.getItem('lastPluvialRiskVisit');
        const response = await getPluvialAlerts({});
        
        if (response?.data?.alerts && lastVisit) {
          const lastVisitDate = new Date(lastVisit);
          const newAlerts = response.data.alerts.filter(
            (alert: PluvialAlert) => new Date(alert.createdAt) > lastVisitDate
          );
          setPluvialAlertsCount(newAlerts.length);
        }
      } catch {
        // Error silencioso, no afecta la UI
      }
    };

    updatePluvialAlertsBadge();
    const interval = setInterval(updatePluvialAlertsBadge, 60000);

    return () => clearInterval(interval);
  }, [location.pathname, setPluvialAlertsCount]);

  // Memoizar las clases CSS para evitar recálculos
  const sidebarClasses = useMemo(() => cn(
    "fixed left-4 top-4 bottom-4 bg-white/40 backdrop-blur-md rounded-3xl shadow-lg z-30 flex flex-col border border-white/20 transition-all duration-300",
    isOpen ? "w-74" : "w-20"
  ), [isOpen]);

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={onToggle}
      className={sidebarClasses}
    >
      <SidebarHeader isOpen={isOpen} onToggle={onToggle} />

      <Navigation isOpen={isOpen} />

      <SidebarFooter isOpen={isOpen} />

      {isOpen && (
        <div className="mt-auto p-4">
          <Footer />
        </div>
      )}
    </Collapsible.Root>
  );
});

Sidebar.displayName = 'Sidebar';
